import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [token, setToken] = useState(params.get("token") || "");
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!params.get("token"));

  useEffect(() => {
    const t = params.get("token");
    if (!t) return;
    (async () => {
      try {
        const { data } = await api.get(`/invites/verify/${t}`);
        setInvite(data);
        setName(data.name || "");
        setCompany(data.company || "");
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setVerifying(false);
      }
    })();
  }, [params]);

  const verifyManual = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get(`/invites/verify/${token}`);
      setInvite(data);
      setName(data.name || "");
      setCompany(data.company || "");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/invites/accept", { token, password, name, company });
      setSession(data.user);
      toast.success("Account activated");
      nav("/fleet?onboarding=1");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="accept-invite-page">
      <Card className="w-full max-w-md p-8">
        <div className="tag-mono text-blue-700 mb-2">ACCEPT INVITE</div>
        <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Activate your account</h2>

        {!invite ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="token">Invite Token</Label>
              <Input id="token" value={token} onChange={(e) => setToken(e.target.value)} data-testid="invite-token-input" />
            </div>
            <Button
              disabled={!token || verifying}
              onClick={verifyManual}
              className="w-full bg-blue-700 hover:bg-blue-800"
              data-testid="verify-invite-button"
            >
              {verifying ? "Verifying…" : "Verify Token"}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
              Invited email: <span className="font-mono">{invite.email}</span>
            </div>
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" />
            </div>
            <div>
              <Label htmlFor="company">Company</Label>
              <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="invite-company-input" />
            </div>
            <div>
              <Label htmlFor="password">Choose Password</Label>
              <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="invite-submit-button">
              {loading ? "Activating…" : "Activate Account"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
